import {validateViewTitle} from '@github-ui/entity-validators'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/issue-viewer/Labels'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {FormControl, Heading, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {MESSAGES} from '../../../constants/messages'
import {useQueryContext, useQueryEditState, useQueryFocus, useQueryViewEditFields} from '../../../contexts/QueryContext'
import type {AppPayload} from '../../../types/app-payload'
import {isDirty} from '../../../utils/type-predicates'
import type {HeaderContentCurrentViewFragment$key} from './__generated__/HeaderContentCurrentViewFragment.graphql'
import styles from './HeaderContent.module.css'
import {IconAndColorPicker} from './IconAndColorPicker'
import {ViewOptionsButton} from './ViewOptionsButton'

type HeaderContentProps = {
  readOnly?: boolean
  currentViewKey: HeaderContentCurrentViewFragment$key
}

export function HeaderContent({readOnly = false, currentViewKey}: HeaderContentProps) {
  const {isCustomView} = useQueryContext()
  const {isEditing} = useQueryEditState()
  const {scoped_repository} = useAppPayload<AppPayload>()

  // Suppress when the outer SidebarPageLayout PageHeader provides the H1.
  // Tracked: https://github.com/github/core-ux/issues/2410
  const sidebarLayoutOwnsTitle = !!scoped_repository && isFeatureEnabled('repo_issues_sidebar_layout')

  const currentViewData = useFragment<HeaderContentCurrentViewFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderContentCurrentViewFragment on Shortcutable {
        name
        description
        id
        ...IconAndColorPickerViewFragment
        ...ViewOptionsButtonCurrentViewFragment
      }
    `,
    currentViewKey,
  )

  const {name: viewName, description: viewDescription, id: viewId} = currentViewData

  const {dirtyTitle, setDirtyTitle, dirtyDescription, setDirtyDescription} = useQueryViewEditFields()
  const {shouldFocusSearchOnNav} = useQueryFocus()
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)

  const viewTitleRef = useRef<HTMLInputElement>(null)

  const onTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const inputTitle = e.target.value
    const inputValidationResult = validateViewTitle(inputTitle)
    setTitleValidationResult(inputValidationResult.errorMessage)
    setDirtyTitle(inputTitle)
  }

  const onDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setDirtyDescription(e.target.value)
  }

  useEffect(() => {
    if (isEditing && viewTitleRef.current && !shouldFocusSearchOnNav) {
      requestAnimationFrame(() => {
        if (viewTitleRef.current) {
          viewTitleRef.current.focus()
        }
      })
    }
  }, [isEditing, shouldFocusSearchOnNav])

  return (
    <div className={styles.HeaderContentContainer}>
      {isEditing ? (
        <div className={styles.editingModeContainer}>
          <div className={styles.iconTitleInputRow}>
            <FormControl>
              <FormControl.Label htmlFor="edit-view-icon-button">{MESSAGES.icon}</FormControl.Label>
              {isCustomView(viewId) && <IconAndColorPicker readOnly={readOnly} currentView={currentViewData} />}
            </FormControl>
            <FormControl className={styles.FormControl}>
              <FormControl.Label>{MESSAGES.title}</FormControl.Label>
              <TextInput
                ref={viewTitleRef}
                onChange={onTitleChange}
                value={isDirty(dirtyTitle) ? dirtyTitle : viewName}
                placeholder={LABELS.viewTitlePlaceholder}
                className={styles.TextInput}
              />
              {titleValidationResult && (
                <FormControl.Validation variant="error">{titleValidationResult}</FormControl.Validation>
              )}
            </FormControl>
          </div>
          <FormControl className={styles.FormControl}>
            <FormControl.Label>{MESSAGES.description}</FormControl.Label>
            <TextInput
              onChange={onDescriptionChange}
              value={isDirty(dirtyDescription) ? dirtyDescription : viewDescription}
              placeholder={LABELS.viewDescriptionPlaceholder}
              className={styles.TextInput}
            />
          </FormControl>
        </div>
      ) : (
        <div className={styles.displayModeContainer}>
          <span className={styles.titleOptionsRow}>
            {isCustomView(viewId) && <IconAndColorPicker readOnly={readOnly} currentView={currentViewData} />}
            <Heading
              as={sidebarLayoutOwnsTitle ? 'h2' : 'h1'}
              className={clsx(scoped_repository ? 'sr-only' : '', styles.Heading)}
            >
              {viewName}
            </Heading>
            {isCustomView(viewId) && !readOnly && <ViewOptionsButton currentView={currentViewData} />}
          </span>
          {viewDescription && <span className={styles.descriptionText}>{viewDescription}</span>}
        </div>
      )}
    </div>
  )
}
